import { useQuery } from "@msuite/ui/query";
import { DataTable } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import type { ServiceFulfillmentPartner } from "@msuite/utils/types";
import type { ClientResponse } from "hono/client";
import { client } from "@/hono";
import type { ServicePanelBaseProps } from "../shared";
import { ServiceFulfillmentPartnersMenu } from "./service-fulfillment-partners-menu";
import { columns } from "./table-config";

/**
 * The server implements `Services.getServiceFulfillmentPartners` but never mounts a
 * `GET /organizations/services/:serviceId/partners` route, so the RPC client has no `$get`
 * on this path and the request 404s at runtime. Typed by hand until the route lands.
 */
interface PartnersGetRoute {
	$get: (args: {
		param: { serviceId: string };
	}) => Promise<
		ClientResponse<{ success: true; partners: ServiceFulfillmentPartner[] }, 200, "json">
	>;
}

export function ServiceFulfillmentPartners({ serviceId }: ServicePanelBaseProps) {
	/** Hooks */

	/** Query */
	const {
		data: partners,
		queryKey,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: ["service", serviceId, "partners"],
		queryFn: async () => {
			const partnersRoute = client.organizations.services[":serviceId"]
				.partners as unknown as PartnersGetRoute;
			const response = await partnersRoute.$get({
				param: { serviceId },
			});
			const data = await responseHandler(response);
			return guardArray(data.partners);
		},
	});

	/** Render */
	return (
		<DataTable
			data={partners}
			isLoading={isLoading}
			columns={columns}
			refetch={refetch}
			persistenceKey="service-fulfillment-partners-table"
			queryKey={queryKey}
			initialColumnVisibility={{
				projects: false,
			}}
			menu={
				<ServiceFulfillmentPartnersMenu
					queryKey={queryKey}
					serviceId={serviceId}
				/>
			}
		/>
	);
}
